import { ChevronRight, Scale } from "lucide-react";
import type { LandDataView, LandRegion, LandRegionRow, LandRegionTier } from "@/lib/api";
import { cn } from "@/lib/cn";
import { SectionCard } from "./shared";

/* -------------------------------------------------------------------------- */
/* 区域研判 — 本市全部市辖区横向对比，推荐 / 可考虑 / 回避 三档                     */
/* -------------------------------------------------------------------------- */

const TIERS: LandRegionTier[] = ["推荐", "可考虑", "回避"];

const TIER_META: Record<LandRegionTier, { pill: string; head: string; hint: string }> = {
  推荐: {
    pill: "bg-good/10 text-good",
    head: "border-good/40",
    hint: "产业需求与拿地条件都强",
  },
  可考虑: {
    pill: "bg-warn/10 text-warn",
    head: "border-warn/40",
    hint: "一强一弱，看条件",
  },
  回避: {
    pill: "bg-zinc-100 text-zinc-500",
    head: "border-zinc-300",
    hint: "两弱或关键数据不足",
  },
};

const ASPECTS: Array<{ key: keyof NonNullable<LandRegion["analysis"]>["aspects"]; label: string }> = [
  { key: "economy", label: "经济强弱" },
  { key: "density", label: "经济密度" },
  { key: "activity", label: "新增企业活跃度" },
  { key: "talent", label: "人口与人才" },
  { key: "industry", label: "主导产业与剥离重化工" },
  { key: "electricity", label: "用电走势" },
  { key: "land", label: "土地供应与地价" },
];

function TierPill({ tier }: { tier: LandRegionTier | "" }): JSX.Element | null {
  if (!tier) return null;
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", TIER_META[tier].pill)}>{tier}</span>
  );
}

// 规则初判依据串(如「GDP体量偏强、新登记增速中等、GDP增速偏弱」) → 强项 / 弱项
function splitBasis(basis: string | undefined): { plus: string[]; minus: string[] } {
  const plus: string[] = [];
  const minus: string[] = [];
  for (const raw of (basis ?? "").split("、")) {
    const m = raw.trim().match(/^(.*?)(偏强|中等|偏弱)$/);
    const name = m?.[1] ?? "";
    if (!m || !name) continue;
    if (m[2] === "偏强") plus.push(name);
    else if (m[2] === "偏弱") minus.push(name);
  }
  return { plus, minus };
}

// 拿地侧的项目名换成直白说法(「地价偏强」= 地价便宜)
const LAND_WORDS: Record<string, [string, string]> = {
  五年供地量: ["五年供地多", "五年供地少"],
  地价: ["地价便宜", "地价偏贵"],
};

function JudgeCell({
  label,
  basis,
  land = false,
}: {
  label: string | undefined;
  basis: string | undefined;
  land?: boolean;
}): JSX.Element {
  const { plus, minus } = splitBasis(basis);
  const word = (name: string, good: boolean): string => (land ? LAND_WORDS[name]?.[good ? 0 : 1] ?? name : name);
  return (
    <td className="px-3 py-2 align-top text-[12px] leading-5 text-zinc-600">
      <div className="text-[13px] font-medium text-zinc-900">{label || "—"}</div>
      {plus.length ? (
        <div>
          <span className="text-zinc-400">{land ? "有利" : "强项"}：</span>
          {plus.map((x) => word(x, true)).join("、")}
        </div>
      ) : null}
      {minus.length ? (
        <div>
          <span className="text-zinc-400">{land ? "不利" : "弱项"}：</span>
          {minus.map((x) => word(x, false)).join("、")}
        </div>
      ) : null}
    </td>
  );
}

/** 各区判断依据: 档位 + 规则初判(产业需求 / 拿地条件)的强弱项; 具体数字在「区县指标对比」。 */
function JudgeTable({ region }: { region: LandRegion }): JSX.Element | null {
  const rows: LandRegionRow[] = region.table;
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-cardline">
      <table className="w-full min-w-[720px] text-[13px]">
        <thead>
          <tr className="whitespace-nowrap bg-cardhead text-left text-[12px] text-zinc-500">
            <th className="px-3 py-2 font-medium">区县</th>
            <th className="px-3 py-2 font-medium">档位</th>
            <th className="px-3 py-2 font-medium">产业需求</th>
            <th className="px-3 py-2 font-medium">拿地条件</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.district}
              className={cn("border-t border-cardline/70", r.is_target ? "bg-brand-50" : i % 2 ? "bg-canvas/60" : "bg-white")}
            >
              <td className={cn("whitespace-nowrap px-3 py-2 align-top", r.is_target ? "font-semibold text-brand-900" : "text-zinc-700")}>
                {r.is_target ? (
                  <span className="mr-1 text-brand-600" title="本项目所在区">
                    ★
                  </span>
                ) : null}
                {r.district}
              </td>
              <td className="whitespace-nowrap px-3 py-2 align-top">
                <TierPill tier={r.tier} />
              </td>
              <JudgeCell label={r.demand_label} basis={r.demand_basis} />
              <JudgeCell label={r.land_label} basis={r.land_basis} land />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RegionSection({ data }: { data: LandDataView }): JSX.Element | null {
  const region = data.region;
  if (!region || region.status === "skipped") return null;
  const a = region.status === "ok" ? region.analysis : null;
  // 档内按对比表顺序(GDP 由高到低)排列
  const order = new Map(region.table.map((r, i) => [r.district, i]));
  const byTier = (tier: LandRegionTier) =>
    (a?.tiers ?? [])
      .filter((t) => t.tier === tier)
      .sort((x, y) => (order.get(x.district) ?? 99) - (order.get(y.district) ?? 99));
  const detailOf = new Map((a?.districts ?? []).map((d) => [d.district, d]));
  const landWindow = region.land.window ? `国信网招拍挂成交 ${region.land.window}` : "";
  const detailOrder = TIERS.flatMap((tier) => byTier(tier).map((t) => t.district));

  return (
    <SectionCard
      icon={<Scale className="h-4 w-4" />}
      id="sec-region"
      title="区域研判"
      count={region.n_districts ? `${region.city} · ${region.n_districts} 个区` : region.city || undefined}
      hint="本市各区横向对比，判断哪些区适合做产业园"
    >
      {!a ? null : (
        <div className="space-y-4">
          <p className="text-[15px] font-medium leading-7 text-zinc-900">{a.headline}</p>

          {a.target ? (
            <div className="rounded-lg border border-brand-100 bg-brand-50 px-4 py-3">
              <div className="flex flex-wrap items-center gap-2 text-[13px]">
                <span className="text-zinc-500">本项目所在区</span>
                <span className="font-semibold text-brand-900">{a.target.district}</span>
                <TierPill tier={a.target.tier} />
              </div>
              <p className="mt-1.5 text-[14px] leading-6 text-zinc-800">{a.target.verdict}</p>
            </div>
          ) : null}

          <div className="grid gap-3 md:grid-cols-3">
            {TIERS.map((tier) => {
              const items = byTier(tier);
              return (
                <div key={tier} className={cn("rounded-lg border-t-2 bg-canvas px-4 py-3", TIER_META[tier].head)}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[14px] font-semibold text-zinc-900">
                      {tier} <span className="font-normal text-zinc-400">{items.length}</span>
                    </span>
                    <span className="text-[11px] text-zinc-400">{TIER_META[tier].hint}</span>
                  </div>
                  {items.length ? (
                    <ul className="mt-2 space-y-2">
                      {items.map((t) => (
                        <li key={t.district} className="text-[13px] leading-5">
                          <span
                            className={cn(
                              "font-medium",
                              t.district === region.target_district ? "text-brand-900" : "text-zinc-800",
                            )}
                          >
                            {t.district === region.target_district ? "★ " : ""}
                            {t.district}
                          </span>
                          <span className="text-zinc-500"> — {t.reason}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-[12px] text-zinc-400">无</p>
                  )}
                </div>
              );
            })}
          </div>

          {a.city_backdrop ? (
            <p className="text-[13px] leading-6 text-zinc-600">
              <span className="mr-1.5 font-medium text-zinc-800">城市背景</span>
              {a.city_backdrop}
            </p>
          ) : null}

          <div className="grid gap-3 md:grid-cols-2">
            {ASPECTS.filter((x) => a.aspects[x.key]).map((x) => (
              <div key={x.key} className="rounded-lg border border-cardline px-4 py-3">
                <div className="text-[12px] font-medium text-zinc-500">{x.label}</div>
                <p className="mt-1 text-[13px] leading-6 text-zinc-800">{a.aspects[x.key]}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {region.table.length ? (
        <div className={a ? "mt-5" : ""}>
          <div className="mb-2 text-[13px] font-medium text-zinc-700">
            各区判断依据
            <span className="ml-2 font-normal text-zinc-400">程序按本市各区中位数初判强弱，具体数字见上方「区县指标对比」</span>
          </div>
          <JudgeTable region={region} />
        </div>
      ) : null}

      {a && a.districts.length ? (
        <div className="mt-5">
          <div className="mb-2 text-[13px] font-medium text-zinc-700">逐区判断</div>
          <div className="divide-y divide-cardline/70 rounded-lg border border-cardline">
            {(detailOrder.length ? detailOrder : a.districts.map((d) => d.district)).map((name) => {
              const d = detailOf.get(name);
              const tier = a.tiers.find((t) => t.district === name);
              if (!d) return null;
              return (
                <details key={name} className="group px-4 py-2.5" open={name === region.target_district}>
                  <summary className="flex cursor-pointer list-none items-center gap-2 text-[13px] [&::-webkit-details-marker]:hidden">
                    <ChevronRight className="h-3.5 w-3.5 text-zinc-400 transition-transform group-open:rotate-90" />
                    <span className={cn("font-medium", name === region.target_district ? "text-brand-900" : "text-zinc-800")}>
                      {name === region.target_district ? "★ " : ""}
                      {name}
                    </span>
                    {tier ? <TierPill tier={tier.tier} /> : null}
                    {tier?.reason ? <span className="truncate text-zinc-500">{tier.reason}</span> : null}
                  </summary>
                  <dl className="mt-2 grid gap-2 pl-5 text-[13px] leading-6 md:grid-cols-3">
                    <div>
                      <dt className="text-[12px] text-zinc-400">产业需求</dt>
                      <dd className="text-zinc-800">{d.demand || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-[12px] text-zinc-400">拿地条件</dt>
                      <dd className="text-zinc-800">{d.land || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-[12px] text-zinc-400">风险与缺数</dt>
                      <dd className="text-zinc-800">{d.risks || "—"}</dd>
                    </div>
                  </dl>
                </details>
              );
            })}
          </div>
        </div>
      ) : null}

      {a && a.gaps.length ? (
        <div className="mt-5 rounded-lg bg-canvas px-4 py-3">
          <div className="text-[12px] font-medium text-zinc-500">研判用到但缺的数据</div>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[12px] leading-5 text-zinc-600">
            {a.gaps.map((g, i) => (
              <li key={i}>{g}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {!a ? (
        <p className="mt-4 rounded-lg bg-canvas px-4 py-3 text-[13px] text-zinc-600">
          本次没有生成研判结论，上表为各区规则初判；可重新发起搜索再试。
        </p>
      ) : null}
      <p className="mt-3 text-[12px] leading-5 text-zinc-400">
        {a
          ? `结论由 AI 根据本页城市、区县数据${landWindow ? `与${landWindow}` : ""}生成，数字以「城市概况」「区县指标对比」为准。`
          : ""}
        地价 = 成交总价 ÷ 成交面积；开发区与行政区重叠，只作佐证不参与排名。
        {region.land.truncated
          ? `国信网成交明细${region.land.incomplete_years?.length ? `（${region.land.incomplete_years.join("、")}年）` : ""}未取全，5 年供地量与排名可能偏低。`
          : ""}
      </p>
    </SectionCard>
  );
}
